<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="sw-header">
        <span class="sw-bar sw-bar--xl"></span>
        <span class="sw-bar sw-bar--md"></span>
      </div>
      <div class="sw-grid">
        <div
          v-for="(q, i) in quadrants"
          :key="q.key"
          class="sw-quad"
          :class="`--${q.key}`"
          :style="{ '--i': i }"
        >
          <span class="sw-quad-label">{{ q.label }}</span>
          <div class="sw-notes">
            <span
              v-for="n in q.count"
              :key="n"
              class="sw-note"
              :style="{ '--n': n - 1 }"
            ></span>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="sw-phone-q"></span>
      <div class="sw-phone-hint">
        Pick a quadrant:
      </div>
      <div class="sw-phone-grid">
        <button
          v-for="(q, i) in quadrants"
          :key="q.key"
          type="button"
          class="sw-phone-quad"
          :class="[`--${q.key}`, { '--tapped': i === tapIndex }]"
          tabindex="-1"
        >
          {{ q.short }}
        </button>
      </div>
      <div class="sw-phone-input">
        <span class="sw-bar sw-bar--md"></span>
        <span class="sw-caret"></span>
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewSwot',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      quadrants: [
        {
          key: 'strengths', label: 'Strengths', short: 'S', count: 3,
        },
        {
          key: 'weaknesses', label: 'Weaknesses', short: 'W', count: 2,
        },
        {
          key: 'opportunities', label: 'Opportunities', short: 'O', count: 3,
        },
        {
          key: 'threats', label: 'Threats', short: 'T', count: 2,
        },
      ],
      tapIndex: 2,
    }
  },
})
</script>

<style scoped lang="scss">
.sw-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.sw-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); height: 8px; }
.sw-bar--md { width: 50%; }

.sw-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.sw-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  flex: 1;
}
.sw-quad {
  position: relative;
  min-height: 64px;
  border-radius: 8px;
  padding: 8px 9px 7px;
  display: flex; flex-direction: column; gap: 6px;
  border: 1px solid rgba(15, 15, 20, 0.08);
}
.sw-quad.--strengths { background: color-mix(in srgb, var(--accent) 10%, white); }
.sw-quad.--weaknesses { background: rgba(15, 15, 20, 0.04); }
.sw-quad.--opportunities { background: rgba(15, 15, 20, 0.04); }
.sw-quad.--threats { background: color-mix(in srgb, var(--accent) 6%, white); }

.sw-quad-label {
  font-size: 9px;
  font-weight: 800;
  color: rgba(15, 15, 20, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.sw-quad.--strengths .sw-quad-label,
.sw-quad.--threats .sw-quad-label { color: var(--accent); }

.sw-notes {
  display: flex; flex-wrap: wrap; gap: 4px;
}
.sw-note {
  display: block;
  width: 22px; height: 18px;
  border-radius: 3px;
  background: var(--accent);
  opacity: 0.7;
  transform: rotate(-2deg);
  animation: sw-stick 4s ease-out infinite;
  animation-delay: calc((var(--i) * 200ms) + (var(--n) * 300ms));
  opacity: 0;
}
.sw-note:nth-child(2n) { transform: rotate(2deg); }

/* phone */
.sw-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.sw-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.sw-phone-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
}
.sw-phone-quad {
  appearance: none;
  height: 36px;
  border-radius: 6px;
  border: 1px solid rgba(15, 15, 20, 0.1);
  background: white;
  color: rgba(15, 15, 20, 0.55);
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}
.sw-phone-quad.--tapped {
  background: var(--accent);
  color: white;
  border-color: var(--accent);
  animation: sw-tap 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.sw-phone-input {
  position: relative;
  height: 30px;
  border-radius: 6px;
  border: 1px solid rgba(15, 15, 20, 0.15);
  padding: 0 8px;
  display: flex; align-items: center;
}
.sw-caret {
  position: absolute;
  right: 8px;
  width: 1px; height: 14px;
  background: var(--accent);
  animation: sw-caret 1s steps(2) infinite;
}

@keyframes sw-stick {
  0%, 10%   { opacity: 0; transform: rotate(-2deg) scale(0.5) translateY(-6px); }
  25%       { opacity: 0.9; transform: rotate(-2deg) scale(1.05) translateY(0); }
  35%, 90%  { opacity: 0.85; transform: rotate(-2deg) scale(1) translateY(0); }
  100%      { opacity: 0.85; transform: rotate(-2deg) scale(1) translateY(0); }
}
@keyframes sw-tap {
  0%, 4%  { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%     { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%    { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}
@keyframes sw-caret {
  0% { opacity: 1; }
  50% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .sw-note, .sw-phone-quad.--tapped, .sw-caret { animation: none; }
  .sw-note { opacity: 0.85; transform: rotate(-2deg) scale(1); }
}
</style>
